import React, { useState, useEffect, useCallback } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  BellAlertIcon,
  BuildingOffice2Icon,
  PaintBrushIcon,
  PaperAirplaneIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useClubSettings } from '../contexts/ClubSettingsContext';
import { clubSettingsAPI, pushAPI, BACKEND_BASE_URL } from '../services/api';
import type { PushStats } from '../types/push';
import { generatePalette } from '../utils/colorPalette';
import { getErrorMessage } from '../utils/errorMessage';
import { logPushTestResult, logPushTestFailure } from '../services/pushDiagnostics';
import ReminderPicker from '../components/events/ReminderPicker';
import type { EventReminderInput } from '../types/event';

const ClubCustomization: React.FC = () => {
  const { settings, refreshSettings } = useClubSettings();
  usePageTitle('Testreszabás');
  const [clubName, setClubName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#2563eb');
  const [defaultReminders, setDefaultReminders] = useState<EventReminderInput[]>([]);
  const [notifyOnCreate, setNotifyOnCreate] = useState(false);
  const [pushStats, setPushStats] = useState<PushStats | null>(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setClubName(settings.clubName || '');
      setPrimaryColor(settings.primaryColor);
      setDefaultReminders(settings.defaultEventReminders ?? []);
      setNotifyOnCreate(settings.notifyOnEventCreateByDefault ?? false);
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await clubSettingsAPI.updateSettings({
        clubName: clubName.trim() || null,
        primaryColor,
      });
      await refreshSettings();
      setSuccess('Beállítások mentve!');
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Hiba történt a mentés során');
    } finally {
      setSaving(false);
    }
  };

  const loadPushStats = useCallback(async () => {
    try {
      setPushStats(await pushAPI.getStats());
    } catch {
      // csak tájékoztató adat, hiba esetén elrejtjük
      setPushStats(null);
    }
  }, []);

  useEffect(() => {
    loadPushStats();
  }, [loadPushStats]);

  /**
   * A teljes diagnosztika a böngésző konzoljára kerül (böngésző és szerver
   * oldal, eszközönkénti eredménnyel) - itt csak a lényeg jelenik meg.
   * A végpont hibánál is 200-at ad, ezért a catch ág már csak hálózati vagy
   * jogosultsági hibát jelent.
   */
  const handleSendTest = async () => {
    setSendingTest(true);
    setTestResult(null);
    try {
      const result = await pushAPI.sendTest();
      await logPushTestResult(result);

      setTestResult({
        ok: result.success,
        text: result.success
          ? `Elküldve ${result.result.delivered}/${result.result.subscriptionsTargeted} eszközre. Részletek a böngésző konzoljában (F12).`
          : `${result.reason ?? 'A teszt értesítés nem ment ki.'} Részletek a böngésző konzoljában (F12).`,
      });
    } catch (err) {
      const message = getErrorMessage(err, 'A teszt értesítést nem sikerült elküldeni.');
      await logPushTestFailure(err, message);
      setTestResult({ ok: false, text: `${message} Részletek a böngésző konzoljában (F12).` });
    } finally {
      setSendingTest(false);
      loadPushStats();
    }
  };

  const handleNotificationSave = async () => {
    setSavingNotifications(true);
    setError(null);
    setSuccess(null);
    try {
      await clubSettingsAPI.updateSettings({
        clubName: clubName.trim() || null,
        primaryColor,
        defaultEventReminders: defaultReminders,
        notifyOnEventCreateByDefault: notifyOnCreate,
      });
      await refreshSettings();
      setSuccess('Értesítési alapbeállítások mentve!');
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Hiba történt a mentés során');
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleClearMlszClub = async () => {
    if (!window.confirm(
      'Biztosan megszünteted az MLSZ egyesület kapcsolatot? A következő importnál újra ki kell választani az egyesületet.'
    )) return;

    setError(null);
    setSuccess(null);
    try {
      await clubSettingsAPI.clearMlszClub();
      await refreshSettings();
      setSuccess('MLSZ egyesület kapcsolat megszüntetve.');
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Hiba történt a művelet során');
    }
  };

  const handleCrestUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      setError('Csak PNG és JPG fájlok engedélyezettek.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('A fájl mérete maximum 2MB lehet.');
      return;
    }

    setUploading(true);
    setError(null);
    try {
      await clubSettingsAPI.uploadCrest(file);
      await refreshSettings();
      setSuccess('Címer feltöltve!');
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Hiba történt a feltöltés során');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteCrest = async () => {
    if (!window.confirm('Biztosan törölni szeretnéd a címert?')) return;
    try {
      await clubSettingsAPI.deleteCrest();
      await refreshSettings();
    } catch {
      setError('Hiba történt a törlés során');
    }
  };

  const palette = generatePalette(primaryColor);

  return (
    <div>
      <div className="flex items-center mb-6">
        <PaintBrushIcon className="h-7 w-7 text-primary-600 mr-3" />
        <h1 className="text-2xl font-bold text-gray-900">Testreszabás</h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">{success}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Club Name & Color */}
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Általános beállítások</h2>

          <div className="space-y-4">
            <div>
              <label htmlFor="clubName" className="block text-sm font-medium text-gray-700 mb-1">
                Klub neve
              </label>
              <input
                id="clubName"
                type="text"
                value={clubName}
                onChange={e => setClubName(e.target.value)}
                placeholder="Portero"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              />
              <p className="text-xs text-gray-400 mt-1">Megjelenik a sidebar-ban és a bejelentkező oldalon</p>
            </div>

            <div>
              <label htmlFor="primaryColor" className="block text-sm font-medium text-gray-700 mb-1">
                Elsődleges szín
              </label>
              <div className="flex items-center gap-3">
                <input
                  id="primaryColor"
                  type="color"
                  value={primaryColor}
                  onChange={e => setPrimaryColor(e.target.value)}
                  className="h-10 w-14 shrink-0 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={e => {
                    const v = e.target.value;
                    if (/^#[0-9a-fA-F]{0,6}$/.test(v)) setPrimaryColor(v);
                  }}
                  className="w-full md:w-28 rounded-md border border-gray-300 px-3 py-2 font-mono text-sm focus:border-primary-500 focus:outline-none"
                  maxLength={7}
                />
              </div>
            </div>

            {/* Color Palette Preview */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Paletta előnézet</p>
              <div className="flex gap-1">
                {Object.entries(palette).map(([shade, color]) => (
                  <div
                    key={shade}
                    className="flex-1 h-8 rounded"
                    style={{ backgroundColor: color }}
                    title={`${shade}: ${color}`}
                  />
                ))}
              </div>
              <div className="flex gap-1 mt-1">
                {Object.keys(palette).map(shade => (
                  <div key={shade} className="flex-1 text-center text-[9px] text-gray-400">{shade}</div>
                ))}
              </div>
            </div>

            <button
              onClick={handleSave}
              disabled={saving || primaryColor.length !== 7}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Mentés...' : 'Mentés'}
            </button>
          </div>
        </div>

        {/* Crest Upload */}
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Klub címer</h2>

          {settings?.crestUrl ? (
            <div className="mb-4">
              <div className="flex flex-col items-start gap-4 md:flex-row md:items-center">
                <img
                  src={`${BACKEND_BASE_URL}${settings.crestUrl}`} onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  alt="Klub címer"
                  className="h-24 w-24 shrink-0 object-contain rounded-lg border border-gray-200 bg-gray-50 p-2"
                />
                <div className="w-full md:w-auto">
                  <p className="text-sm text-gray-600 mb-2">Jelenlegi címer</p>
                  <button
                    onClick={handleDeleteCrest}
                    className="flex w-full items-center justify-center gap-1 min-h-[36px] px-3 py-2 border border-red-300 text-red-700 rounded-md text-sm hover:bg-red-50 md:w-auto md:justify-start md:py-1.5"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Törlés
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-4 flex items-center justify-center h-24 w-24 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
              <p className="text-xs text-gray-400 text-center px-2">Nincs címer</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {settings?.crestUrl ? 'Címer cseréje' : 'Címer feltöltése'}
            </label>
            <input
              type="file"
              accept=".png,.jpg,.jpeg"
              onChange={handleCrestUpload}
              disabled={uploading}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 disabled:opacity-50 md:file:py-2"
            />
            <p className="text-xs text-gray-400 mt-1">PNG vagy JPG, maximum 2MB</p>
            {uploading && <p className="text-sm text-primary-600 mt-2">Feltöltés...</p>}
          </div>
        </div>
      </div>

      {/* Esemény értesítések alapbeállítása */}
      <div className="bg-white rounded-lg shadow p-4 md:p-6 mt-6">
        <div className="flex items-center mb-4">
          <BellAlertIcon className="h-6 w-6 text-primary-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Esemény értesítések</h2>
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Alapértelmezett emlékeztetők</p>
            <p className="text-xs text-gray-400 mb-3">
              Minden új esemény és edzés ezekkel az emlékeztetőkkel jön létre. Az edző eseményenként
              felülírhatja őket.
            </p>
            <ReminderPicker value={defaultReminders} onChange={setDefaultReminders} />
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={notifyOnCreate}
              onChange={e => setNotifyOnCreate(e.target.checked)}
              className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span>
              <span className="block text-sm font-medium text-gray-700">
                Azonnali értesítés új esemény felvételekor
              </span>
              <span className="block text-xs text-gray-400">
                Az esemény létrehozásakor rögtön push értesítést kapnak az érintettek.
              </span>
            </span>
          </label>

          <button
            onClick={handleNotificationSave}
            disabled={savingNotifications}
            className="w-full md:w-auto px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
          >
            {savingNotifications ? 'Mentés...' : 'Mentés'}
          </button>

          {/* Próba értesítés + állapot. Ebből azonnal látszik, ha azért nem
              megy ki semmi, mert nincs feliratkozott eszköz. */}
          <div className="border-t border-gray-100 pt-4 mt-2">
            <p className="text-sm font-medium text-gray-700 mb-1">Működik az értesítés?</p>
            <p className="text-xs text-gray-400 mb-3">
              A próba értesítés a <strong>saját eszközeidre</strong> megy ki. Ha nem érkezik meg,
              előbb kapcsold be a push értesítéseket a Profil oldalon.
            </p>

            {pushStats && (
              <div className="mb-3 space-y-1 text-xs">
                {!pushStats.vapidConfigured && (
                  <p className="text-amber-700">
                    A szerveren nincsenek beállítva a VAPID kulcsok - így egyetlen értesítés sem megy ki.
                  </p>
                )}
                <p className="text-gray-500">
                  Feliratkozott eszközök a klubban:{' '}
                  <strong className={pushStats.subscribedDevices === 0 ? 'text-amber-700' : 'text-gray-700'}>
                    {pushStats.subscribedDevices}
                  </strong>{' '}
                  ({pushStats.subscribedUsers} / {pushStats.activeUsers} felhasználó)
                </p>
                <p className="text-gray-500">
                  Ebből a tiéd:{' '}
                  <strong className={pushStats.ownDevices === 0 ? 'text-amber-700' : 'text-gray-700'}>
                    {pushStats.ownDevices}
                  </strong>
                </p>
                {pushStats.subscribedDevices === 0 && (
                  <p className="text-amber-700">
                    Amíg senki nincs feliratkozva, az eseményekről sem tud értesítés kimenni -
                    ez önmagában nem hiba a rendszerben.
                  </p>
                )}
              </div>
            )}

            <button
              onClick={handleSendTest}
              disabled={sendingTest}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 md:w-auto"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
              {sendingTest ? 'Küldés...' : 'Próba értesítés küldése'}
            </button>

            {testResult && (
              <p className={`mt-2 text-sm ${testResult.ok ? 'text-green-700' : 'text-red-700'}`}>
                {testResult.text}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* MLSZ (IFA) egyesület kapcsolat */}
      <div className="bg-white rounded-lg shadow p-4 md:p-6 mt-6">
        <div className="flex items-center mb-4">
          <BuildingOffice2Icon className="h-6 w-6 text-primary-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">MLSZ (IFA) egyesület</h2>
        </div>

        {settings?.mlszClubId ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 break-words">
                {settings.mlszClubName || 'Ismeretlen egyesület'}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Azonosító: {settings.mlszClubId} · Az importok ennek az egyesületnek a
                játékosait hozzák be.
              </p>
            </div>
            <button
              onClick={handleClearMlszClub}
              className="flex w-full items-center justify-center gap-1 rounded-md border border-red-300 px-3 py-2 text-sm text-red-700 hover:bg-red-50 sm:w-auto"
            >
              <TrashIcon className="h-4 w-4" />
              Kapcsolat törlése
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            Még nincs egyesület hozzárendelve. Az IFA oldalon az első sikeres lekérdezéskor
            automatikusan rögzül, hogy melyik egyesületből importálunk.
          </p>
        )}
      </div>
    </div>
  );
};

export default ClubCustomization;
